import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Grid, Typography } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import BasicDialog from "../../../common/components/functional/dialogs/BasicDialog";
import CommonSelectField from "../../../common/components/functional/fields/selectField/CommonSelectField";
import FormGridRow from "../../../common/components/functional/formGridRow/FormGridRow";
import InputHelperText from "../../../common/components/functional/error/InputHelperText";
import { FlexVerticalAligned } from "../../../common/components/styled/styled";
import isEmpty from "../../../common/helpers/isEmpty";
import { Nullable } from "../../../common/types/nullable";
import {
  YES_NO_OPTIONS,
  YesNoOptions,
} from "../../customer/contract/constants";
import { OrderShiftList } from "../components/OrderShiftList";
import {
  ASK_REPLACEMENT_TALENT_REASONS,
  CancelAssignmentScope,
  OrderDialogMode,
} from "../constants";
import { AutomatchingStatus } from "../orderTypes";
import { ShiftSummary } from "../types/editOrder";

interface Props {
  isOpen: boolean;
  closeDialog: () => void;
  onCancel: (
    reasonForCancellation: string,
    replaceTalent?: boolean,
    setAutomatchingStatus?: AutomatchingStatus,
  ) => void;
  cancelOptions: { id: string; value: string }[];
  isLoading: boolean;
  openMode?: OrderDialogMode;
  isFutureShift?: boolean;
  disableTalentReplacement?: boolean;
  cancelAssignmentScope?: CancelAssignmentScope;
  cancelledShifts?: ShiftSummary[];
  isBundleOrder?: boolean;
  cancelAssignmentPaySalary?: boolean;
  isAutomatchingOn?: boolean;
}

export const OrderCancellationDialog = ({
  isOpen,
  closeDialog,
  onCancel,
  cancelOptions,
  isLoading,
  openMode,
  isFutureShift = false,
  disableTalentReplacement = false,
  cancelAssignmentScope = CancelAssignmentScope.Single,
  cancelledShifts = [],
  isBundleOrder = false,
  cancelAssignmentPaySalary,
  isAutomatchingOn = false,
}: Props) => {
  const [cancellationReason, setCancellationReason] = useState("");
  const [shouldAskReplacementTalent, setShouldAskReplacementTalent] =
    useState<Nullable<boolean>>(null);
  const [shouldReplaceTalent, setShouldReplaceTalent] =
    useState<Nullable<boolean>>(null);
  const [automatchingStatus, setAutomatchingStatus] =
    useState<Nullable<AutomatchingStatus>>(null);

  const { t } = useTranslation(["common", "order"]);

  useEffect(() => {
    // Only allow replacement flow for bundles when cancelling all shifts at once.
    const shouldAllowReplacementFlow =
      !isBundleOrder || cancelAssignmentScope === CancelAssignmentScope.Bundle;

    const canAskReplacement =
      !disableTalentReplacement &&
      openMode === OrderDialogMode.CancelAssignment &&
      shouldAllowReplacementFlow &&
      isFutureShift &&
      ASK_REPLACEMENT_TALENT_REASONS.includes(cancellationReason);

    setShouldAskReplacementTalent(canAskReplacement);

    if (!canAskReplacement) {
      setShouldReplaceTalent(null);
      setAutomatchingStatus(null);
    }

    // reset states when dialog is closed
    if (cancellationReason === "") {
      setShouldAskReplacementTalent(null);
      setShouldReplaceTalent(null);
      setAutomatchingStatus(null);
    }
  }, [
    cancellationReason,
    cancelAssignmentScope,
    disableTalentReplacement,
    isBundleOrder,
    isFutureShift,
    openMode,
  ]);

  useEffect(() => {
    if (!shouldReplaceTalent) {
      setAutomatchingStatus(null);
    } else if (isAutomatchingOn) {
      setAutomatchingStatus(AutomatchingStatus.On);
    }
  }, [shouldReplaceTalent, isAutomatchingOn]);

  const onConfirm = () => {
    const replaceTalent = shouldReplaceTalent ?? false;
    const selectedAutomatchingStatus =
      replaceTalent && automatchingStatus === AutomatchingStatus.On
        ? AutomatchingStatus.On
        : undefined;
    onCancel(cancellationReason, replaceTalent, selectedAutomatchingStatus);
  };

  const onCancelDialog = () => {
    setCancellationReason("");
    closeDialog();
  };

  const handleCancellationReasonChange = (
    event: SelectChangeEvent<string | string[]>,
  ) => setCancellationReason(event.target.value as string);

  const handleShouldReplaceTalentChange = (
    event: SelectChangeEvent<string | string[]>,
  ) =>
    setShouldReplaceTalent(() => {
      if (event.target.value === YesNoOptions.Yes) return true;
      if (event.target.value === YesNoOptions.No) return false;
      return null;
    });

  const handleAutomatchingStatusChange = (
    event: SelectChangeEvent<string | string[]>,
  ) =>
    setAutomatchingStatus(
      event.target.value === YesNoOptions.Yes
        ? AutomatchingStatus.On
        : event.target.value === YesNoOptions.No
          ? AutomatchingStatus.Off
          : null,
    );

  const getAutomatchingStatusValue = (
    automatchingStatus?: Nullable<AutomatchingStatus>,
  ) => {
    if (!automatchingStatus) return "";

    return automatchingStatus === AutomatchingStatus.On
      ? YesNoOptions.Yes
      : YesNoOptions.No;
  };

  const getShouldReplaceTalentValue = () => {
    if (shouldReplaceTalent === null) return "";
    return shouldReplaceTalent ? YesNoOptions.Yes : YesNoOptions.No;
  };

  const confirmDisabled =
    !cancellationReason ||
    (shouldAskReplacementTalent &&
      (shouldReplaceTalent === null ||
        (shouldReplaceTalent && !automatchingStatus)));

  const cancelledShiftItems =
    cancelAssignmentScope === CancelAssignmentScope.Bundle
      ? cancelledShifts
      : [];

  const automatchingHelperText =
    automatchingStatus === AutomatchingStatus.On
      ? t("order:automatchingOfferHelperTextOn")
      : automatchingStatus === AutomatchingStatus.Off
        ? t("order:automatchingOfferHelperTextOff")
        : undefined;

  return (
    <BasicDialog
      open={isOpen}
      title={t("order:confirmCancellation")}
      PaperProps={{ sx: { width: "697px" } }}
      onCancel={onCancelDialog}
      onConfirm={onConfirm}
      confirmDisabled={!!confirmDisabled}
      content={
        <Grid container rowGap={0.5}>
          {cancelAssignmentPaySalary !== undefined && (
            <FlexVerticalAligned gap="4px">
              <Typography variant="secondary">
                {`${t("order:paymentLabel")}:`}
              </Typography>
              <Typography>
                {cancelAssignmentPaySalary
                  ? t("order:paidButNotBilled")
                  : t("order:notPaid")}
              </Typography>
            </FlexVerticalAligned>
          )}
          {!isEmpty(cancelledShiftItems) && (
            <Grid item xs={12} mt={1} mb={2}>
              <OrderShiftList
                title={t("order:shiftsToCancel")}
                items={cancelledShiftItems}
                maxHeight="160px"
              />
            </Grid>
          )}
          <FormGridRow
            label={t("order:chooseCancellationReason")}
            labelPosition="top"
          >
            <CommonSelectField
              value={cancellationReason}
              onChange={handleCancellationReasonChange}
              options={cancelOptions}
              getOptionLabel={(opt) => t(`order:${opt.id}`)}
              getOptionValue={(opt) => opt.value}
            />
          </FormGridRow>
          {shouldAskReplacementTalent && (
            <>
              <FormGridRow
                label={t("order:shouldAskReplacementTalent")}
                labelPosition="top"
              >
                <CommonSelectField
                  value={getShouldReplaceTalentValue()}
                  onChange={handleShouldReplaceTalentChange}
                  options={YES_NO_OPTIONS}
                  getOptionLabel={(opt) => t(`common:${opt}`)}
                  getOptionValue={(opt) => opt}
                />
              </FormGridRow>
              {shouldReplaceTalent === true && (
                <>
                  <FormGridRow
                    label={t("order:shouldStartAutomatching")}
                    labelPosition="top"
                  >
                    <CommonSelectField
                      value={getAutomatchingStatusValue(automatchingStatus)}
                      onChange={handleAutomatchingStatusChange}
                      options={YES_NO_OPTIONS}
                      getOptionLabel={(opt) =>
                        opt === YesNoOptions.No
                          ? t("order:automatchingNoOfferPreviouslyOnly")
                          : t(`common:${opt}`)
                      }
                      getOptionValue={(opt) => opt}
                      disabled={isAutomatchingOn}
                    />
                  </FormGridRow>
                  <InputHelperText
                    helperText={
                      isAutomatchingOn
                        ? t("order:automatchingAlreadyActive")
                        : automatchingHelperText
                    }
                  />
                </>
              )}
            </>
          )}
        </Grid>
      }
    />
  );
};
